import { cn } from '@/lib/utils'

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'premium'

const variantStyles: Record<BadgeVariant, { bg: string; text: string }> = {
  success: { bg: 'var(--color-success-light)', text: 'var(--color-success-dark)' },
  warning: { bg: 'var(--color-warning-light)', text: 'var(--color-warning-dark)' },
  error: { bg: 'var(--color-error-light)', text: 'var(--color-error-dark)' },
  info: { bg: 'var(--color-info-light)', text: 'var(--color-info-dark)' },
  neutral: { bg: 'var(--color-gray-100)', text: 'var(--color-gray-600)' },
  premium: { bg: 'var(--color-brand-primary-50)', text: 'var(--color-brand-primary)' },
}

interface BadgeProps {
  variant?: BadgeVariant
  children: React.ReactNode
  className?: string
}

export function Badge({ variant = 'neutral', children, className }: BadgeProps) {
  const style = variantStyles[variant]
  return (
    <span
      className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', className)}
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {children}
    </span>
  )
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: BadgeVariant; label: string }> = {
    active: { variant: 'success', label: 'Activo' },
    pending: { variant: 'warning', label: 'Pendiente' },
    error: { variant: 'error', label: 'Error' },
    disconnected: { variant: 'neutral', label: 'Desconectado' },
    success: { variant: 'success', label: 'Completado' },
    partial: { variant: 'warning', label: 'Parcial' },
    failed: { variant: 'error', label: 'Fallido' },
    queued: { variant: 'info', label: 'En cola' },
    running: { variant: 'info', label: 'Ejecutando' },
    trial: { variant: 'info', label: 'Trial' },
    suspended: { variant: 'error', label: 'Suspendido' },
    cancelled: { variant: 'neutral', label: 'Cancelado' },
    invited: { variant: 'info', label: 'Invitado' },
    disabled: { variant: 'neutral', label: 'Deshabilitado' },
  }
  const entry = map[status] ?? { variant: 'neutral' as BadgeVariant, label: status }
  return <Badge variant={entry.variant}>{entry.label}</Badge>
}

export function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, BadgeVariant> = {
    critical: 'error',
    high: 'warning',
    medium: 'info',
    low: 'neutral',
  }
  return <Badge variant={map[severity] ?? 'neutral'}>{severity}</Badge>
}
